// VRG-734 — GTM → Content marketing → VERAfied. Canonical shapes: `features/gtm/verafied/verafied.service.ts`.
import type {
  VerafiedConnection,
  VerafiedEpisodeStatus,
  VerafiedEvaluation,
  VerafiedGuestStatus,
  VerafiedGuestType,
  VerafiedInfluence,
  VerafiedLinkKind,
  VerafiedLinkState,
} from '@repo/types'

import { gtmCall as call } from './gtmApi'

export interface VerafiedRef {
  id: string
  name: string
}

export interface VerafiedGuest {
  id: string
  guest_type: VerafiedGuestType
  name: string
  role_title: string | null
  company_name: string | null
  lead: { id: string; name: string | null; email: string; title: string | null } | null
  sales_account: VerafiedRef | null
  company: VerafiedRef | null
  status: VerafiedGuestStatus
  status_changed_at: string
  influence: VerafiedInfluence | null
  connection: VerafiedConnection | null
  photo_url: string | null
  industry: string | null
  company_type: string | null
  /** SENSITIVE — internal only; never exported (the guest table leaves it out of Download). */
  notes: string | null
  check_required: string | null
  import_notes: string[]
  links: Array<{ id: string; kind: VerafiedLinkKind; url: string }>
  episodes: Array<{ id: string; title: string; status: VerafiedEpisodeStatus; state: VerafiedLinkState }>
  created_at: string
  updated_at: string
}

export interface VerafiedEpisode {
  id: string
  title: string
  description: string | null
  evaluation: VerafiedEvaluation | null
  status: VerafiedEpisodeStatus
  status_changed_at: string
  solutions: string[]
  industries: string[]
  recording_on: string | null
  publish_on: string | null
  length_seconds: number | null
  video_url: string | null
  thumbnail_url: string | null
  has_trailer: boolean
  insights_slug: string | null
  notes: string | null
  claims_check: string | null
  import_notes: string[]
  /** A pending model suggestion (spec §5), applied only when someone approves it. */
  suggestion: { solutions: string[]; industries: string[]; reason: string | null; model: string | null; suggested_at: string } | null
  guests: Array<{ id: string; name: string; state: VerafiedLinkState; status: VerafiedGuestStatus }>
  segments: Array<{ id: string; name: string; position: number }>
  created_at: string
  updated_at: string
}

export interface VerafiedSegment {
  id: string
  slug: string
  name: string
  purpose: string | null
  typical_minutes: number | null
  needs: string | null
  position: number
  episodes: Array<{ id: string; title: string; status: VerafiedEpisodeStatus }>
}

export interface VerafiedPersonMatch {
  kind: 'lead' | 'sales_account' | 'company'
  id: string
  name: string
  detail: string | null
  guest_id: string | null
}

export type VerafiedGuestInput = Partial<
  Pick<
    VerafiedGuest,
    'guest_type' | 'name' | 'role_title' | 'company_name' | 'status' | 'influence' | 'connection' | 'industry' | 'company_type' | 'notes' | 'check_required'
  >
> & {
  /** Clear only: a photo is set by `uploadGuestPhoto`. */
  photo_url?: null
  lead_id?: string | null
  sales_account_id?: string | null
  company_id?: string | null
  links?: Array<{ kind?: VerafiedLinkKind; url: string }>
  /** `[]` clears the importer's notes once someone has confirmed them. */
  import_notes?: []
}

export type VerafiedEpisodeInput = Partial<
  Pick<
    VerafiedEpisode,
    | 'title'
    | 'description'
    | 'evaluation'
    | 'status'
    | 'solutions'
    | 'industries'
    | 'recording_on'
    | 'publish_on'
    | 'length_seconds'
    | 'video_url'
    | 'thumbnail_url'
    | 'has_trailer'
    | 'insights_slug'
    | 'notes'
    | 'claims_check'
  >
> & { import_notes?: [] }

export type VerafiedSegmentInput = Partial<Pick<VerafiedSegment, 'name' | 'purpose' | 'typical_minutes' | 'needs'>>

const base = '/internal/gtm/verafied'
const id = (v: string) => encodeURIComponent(v)

export const verafiedApi = {
  guests: (): Promise<VerafiedGuest[]> => call('GET', `${base}/guests`, 'load guests'),
  people: (q: string): Promise<VerafiedPersonMatch[]> => call('GET', `${base}/people?q=${encodeURIComponent(q)}`, 'search people'),
  createGuest: (input: VerafiedGuestInput) => call<{ id: string }>('POST', `${base}/guests`, 'add the guest', input),
  updateGuest: (guestId: string, input: VerafiedGuestInput) => call<{ ok: true }>('PATCH', `${base}/guests/${id(guestId)}`, 'save the guest', input),
  deleteGuest: (guestId: string) => call<{ ok: true }>('DELETE', `${base}/guests/${id(guestId)}`, 'remove the guest'),
  /** Photos are uploaded only (spec §1); a write can clear `photo_url` but never set it. */
  uploadGuestPhoto: (guestId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return call<{ photo_url: string }>('POST', `${base}/guests/${id(guestId)}/photo`, 'upload the photo', form)
  },

  episodes: (): Promise<VerafiedEpisode[]> => call('GET', `${base}/episodes`, 'load episodes'),
  createEpisode: (input: VerafiedEpisodeInput) => call<{ id: string }>('POST', `${base}/episodes`, 'add the episode', input),
  updateEpisode: (episodeId: string, input: VerafiedEpisodeInput) =>
    call<{ ok: true }>('PATCH', `${base}/episodes/${id(episodeId)}`, 'save the episode', input),
  deleteEpisode: (episodeId: string) => call<{ ok: true }>('DELETE', `${base}/episodes/${id(episodeId)}`, 'delete the episode'),
  setEpisodeGuest: (episodeId: string, guestId: string, state: VerafiedLinkState) =>
    call<{ ok: true }>('PUT', `${base}/episodes/${id(episodeId)}/guests/${id(guestId)}`, 'link the guest', { state }),
  removeEpisodeGuest: (episodeId: string, guestId: string) =>
    call<{ ok: true }>('DELETE', `${base}/episodes/${id(episodeId)}/guests/${id(guestId)}`, 'unlink the guest'),
  setEpisodeSegments: (episodeId: string, segmentIds: string[]) =>
    call<{ ok: true }>('PUT', `${base}/episodes/${id(episodeId)}/segments`, 'save the running order', { segment_ids: segmentIds }),

  /** No `run` here on purpose: the suggestion pass is a paid call and is never one click away. */
  applySuggestions: (ids: string[]) => call<{ applied: number }>('POST', `${base}/episodes/suggestions/apply`, 'apply the suggestions', { ids }),
  dismissSuggestions: (ids: string[]) =>
    call<{ dismissed: number }>('POST', `${base}/episodes/suggestions/dismiss`, 'dismiss the suggestions', { ids }),

  segments: (): Promise<VerafiedSegment[]> => call('GET', `${base}/segments`, 'load segments'),
  createSegment: (input: VerafiedSegmentInput) => call<{ id: string }>('POST', `${base}/segments`, 'add the segment', input),
  updateSegment: (segmentId: string, input: VerafiedSegmentInput) =>
    call<{ ok: true }>('PATCH', `${base}/segments/${id(segmentId)}`, 'save the segment', input),
  deleteSegment: (segmentId: string) => call<{ ok: true }>('DELETE', `${base}/segments/${id(segmentId)}`, 'delete the segment'),

  /** The CSV TEXT travels in the body; the files are never uploaded or stored. */
  importSheets: (input: { guests_csv?: string; episodes_csv?: string }): Promise<VerafiedImportResult> =>
    call('POST', `${base}/import`, 'import the sheets', input),
}

export interface VerafiedImportResult {
  guests: { created: number; existing: number; dropped: string[]; linked_to_people: number; linked_to_companies: number }
  episodes: { created: number; existing: number }
  episode_links: { created: number; unmatched: string[] }
}
